import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';

import {
  SHRED_COLORS,
  SHRED_COPY,
  SHRED_FEED_ICONS,
  SHRED_FONTS,
  SHRED_PRIZE_IDS,
  SHRED_STATUS,
  SHRED_TV_PRIZE_DROP_SHOW_MS,
  SHRED_TV_VAULT_ANIM_MS,
  SHRED_TV_ROTATE_MS,
  SHRED_TV_VAULT_FIRST_UNLOCK_MS,
  SHRED_TV_VAULT_UNLOCK_STEP_MS,
  SHRED_TV_TOP_COUNT,
} from '../constants';
import {
  buildFeedSentence,
  formatCompact,
  formatCountdown,
  formatPct,
  getCountdownTarget,
  getCrewProgress,
  getCurrentDay,
  getNextPrizeDropDelayMs,
  getPodiumEntries,
  getPrizeList,
  getRanked,
  getTopRookie,
  isRookie,
  pickRandomPrize,
  shuffleWithSeed,
} from '../helpers';
import { useNow } from '../hooks';
import { getItemArtKey, ItemArt, PrizeIcon } from './PrizeArt';
import { IneligibleChip, LevelChip, MemberAvatar, pctColor } from './shared';

const PANELS = SHRED_COPY.TV_PANELS;
const SEED_RANGE = 1000000;

const STAGE_WIDTH = { landscape: 1920, portrait: 1080 };

// The display is laid out on a fixed-width stage (1920 px wide for a landscape TV, 1080 px for a vertical
// one) and scaled to the real screen, so every size below is in design pixels and the layout looks the
// same on any TV resolution. The stage height follows the screen's aspect ratio, so nothing is letterboxed.
const SIZES = {
  landscape: {
    padX: 77, padY: 32, title: 86, day: 32, panelTitle: 54, panelTitleGap: 16, dots: 16,
    podiumGap: 32, podiumAvatar: [150, 110, 110], podiumName: [54, 43, 43], podiumPct: [65, 49, 49], podiumLevel: 19, podiumRow: false,
    boardCols: 2, boardRank: 54, boardRankW: 96, boardAvatar: 56, boardName: 35, boardPct: 41, boardLevel: 17, boardGap: 13,
    crewPct: 151, crewText: 37, crewBar: 65,
    feedText: 37, feedDay: 22, feedGap: 22, feedCount: 7,
    countdownLabel: 32, countdownValue: 173, countdownText: 37, status: 43, rookie: 32,
    vaultCols: 3, vaultIcon: 86, vaultTitle: 40, vaultPrize: 28, vaultCrewTitle: 44, vaultCrewText: 30, dropIcon: 90, dropTitle: 52, dropText: 34, dropLabel: 28,
  },
  portrait: {
    padX: 60, padY: 56, title: 130, day: 40, panelTitle: 84, panelTitleGap: 28, dots: 24,
    podiumGap: 36, podiumAvatar: [240, 180, 180], podiumName: [92, 72, 72], podiumPct: [96, 72, 72], podiumLevel: 30, podiumRow: true,
    boardCols: 1, boardRank: 56, boardRankW: 104, boardAvatar: 84, boardName: 46, boardPct: 52, boardLevel: 24, boardGap: 14,
    crewPct: 300, crewText: 52, crewBar: 90,
    feedText: 50, feedDay: 30, feedGap: 36, feedCount: 8,
    countdownLabel: 44, countdownValue: 230, countdownText: 50, status: 60, rookie: 44,
    vaultCols: 2, vaultIcon: 84, vaultTitle: 44, vaultPrize: 30, vaultCrewTitle: 58, vaultCrewText: 36, dropIcon: 120, dropTitle: 68, dropText: 44, dropLabel: 36,
  },
};

const METALS = ['#ffc233', '#cfd6e0', '#d9894a'];

export const getStageLayout = (width, height) => {
  const portrait = height > width;
  const stageWidth = portrait ? STAGE_WIDTH.portrait : STAGE_WIDTH.landscape;
  const scale = width > 0 ? width / stageWidth : 1;
  return { portrait, stageWidth, scale, stageHeight: height > 0 ? height / scale : stageWidth };
};

const readViewport = () => ({
  width: typeof window === 'undefined' ? 1920 : window.innerWidth,
  height: typeof window === 'undefined' ? 1080 : window.innerHeight,
});

const useStageLayout = () => {
  const [viewport, setViewport] = useState(readViewport);
  useLayoutEffect(() => {
    const onResize = () => setViewport(readViewport());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
    };
  }, []);
  return getStageLayout(viewport.width, viewport.height);
};

const PodiumPanel = ({ entries, sizes }) => {
  const podium = getPodiumEntries(entries);
  const rookie = getTopRookie(entries);
  const order = sizes.podiumRow ? [0, 1, 2] : [1, 0, 2];
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${sizes.podiumGap}px`, height: '100%', justifyContent: 'center' }}>
      <Box sx={sizes.podiumRow ? { display: 'flex', flexDirection: 'column', gap: `${sizes.podiumGap}px` } : { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '38px', alignItems: 'end' }}>
        {order.map((index) => {
          const entry = podium[index];
          const first = index === 0;
          if (sizes.podiumRow) {
            return (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: '36px', minWidth: 0, px: '32px', py: first ? '52px' : '36px', borderRadius: '28px', border: `3px solid ${METALS[index]}66`, background: `linear-gradient(90deg, ${METALS[index]}26, transparent)` }}>
                <Typography sx={{ width: '84px', textAlign: 'center', fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: first ? '140px' : '104px', lineHeight: 1, color: METALS[index] }}>{index + 1}</Typography>
                {entry ? (
                  <>
                    <MemberAvatar id={entry.id} name={entry.username} size={sizes.podiumAvatar[index]} ring={METALS[index]} />
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography noWrap sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: `${sizes.podiumName[index]}px`, lineHeight: 1.1 }}>{entry.username}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: '24px', mt: '8px' }}>
                        <LevelChip level={entry.level} sx={{ fontSize: `${sizes.podiumLevel}px` }} />
                        <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.podiumPct[index]}px`, lineHeight: 1.1, color: pctColor(entry.improvementPct) }}>{formatPct(entry.improvementPct)}</Typography>
                      </Box>
                    </Box>
                  </>
                ) : (
                  <Typography sx={{ color: SHRED_COLORS.muted, fontSize: '52px' }}>{SHRED_COPY.PODIUM_EMPTY}</Typography>
                )}
              </Box>
            );
          }
          return (
            <Box key={index} sx={{ textAlign: 'center', pb: first ? '43px' : index === 1 ? '22px' : 0 }}>
              {entry ? (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <MemberAvatar id={entry.id} name={entry.username} size={sizes.podiumAvatar[index]} ring={METALS[index]} />
                  </Box>
                  <Typography noWrap sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: `${sizes.podiumName[index]}px`, mt: 1 }}>
                    {entry.username}
                  </Typography>
                  <LevelChip level={entry.level} sx={{ fontSize: `${sizes.podiumLevel}px` }} />
                  <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.podiumPct[index]}px`, color: pctColor(entry.improvementPct) }}>
                    {formatPct(entry.improvementPct)}
                  </Typography>
                </>
              ) : (
                <Typography sx={{ color: SHRED_COLORS.muted, fontSize: '32px' }}>{SHRED_COPY.PODIUM_EMPTY}</Typography>
              )}
              <Box sx={{ mt: 1.5, height: first ? '130px' : index === 1 ? '86px' : '54px', borderTop: `4px solid ${METALS[index]}`, background: `linear-gradient(180deg, ${METALS[index]}33, transparent)`, display: 'grid', placeItems: 'center', fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: '65px', color: METALS[index] }}>
                {index + 1}
              </Box>
            </Box>
          );
        })}
      </Box>
      {rookie && (
        <Typography sx={{ textAlign: 'center', fontFamily: SHRED_FONTS.body, fontSize: `${sizes.rookie}px`, color: '#d9bcff' }}>
          🎃 {SHRED_COPY.ROOKIE_STAR_TITLE}: <b>{rookie.username}</b> {rookie.level ? `(Lv ${rookie.level}) ` : ''}
          <Box component="span" sx={{ color: pctColor(rookie.improvementPct) }}>{formatPct(rookie.improvementPct)}</Box>
        </Typography>
      )}
    </Box>
  );
};

const byImprovement = (a, b) => b.improvementPct - a.improvementPct || b.sessions - a.sessions;

export const getTvBoardRows = (entries) => {
  const ranked = getRanked(entries).filter((e) => !isRookie(e));
  if (ranked.length > 0) {
    return { rows: ranked.slice(0, SHRED_TV_TOP_COUNT), lineup: false };
  }
  return {
    rows: entries.filter((e) => !e.qualified && !isRookie(e)).sort(byImprovement).slice(0, SHRED_TV_TOP_COUNT),
    lineup: true,
  };
};

export const getTvSpotlightRows = (entries, seed) => shuffleWithSeed(entries, seed).slice(0, SHRED_TV_TOP_COUNT);

const BoardRows = ({ rows, lineup, minSessions, sizes }) => {
  const perColumn = Math.ceil(SHRED_TV_TOP_COUNT / sizes.boardCols);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${sizes.boardCols}, minmax(0, 1fr))`, gridAutoFlow: 'column', gridTemplateRows: `repeat(${perColumn}, 1fr)`, columnGap: '58px', rowGap: `${sizes.boardGap}px`, height: '100%' }}>
      {rows.map((entry) => (
        <Box key={entry.id} sx={{ display: 'flex', alignItems: 'center', gap: '23px', minWidth: 0, px: '19px', borderRadius: '16px', background: !lineup && entry.rank <= 3 ? 'rgba(255,90,31,0.12)' : 'rgba(255,255,255,0.04)' }}>
          <Typography sx={{ width: `${sizes.boardRankW}px`, flex: 'none', fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: lineup ? `${Math.round(sizes.boardRank * 0.68)}px` : `${sizes.boardRank}px`, color: entry.rank === 1 ? SHRED_COLORS.gold : SHRED_COLORS.text }}>{lineup ? `${Math.min(entry.sessions, minSessions)}/${minSessions}` : entry.rank}</Typography>
          <MemberAvatar id={entry.id} name={entry.username} size={sizes.boardAvatar} />
          <Typography noWrap sx={{ flex: 1, minWidth: 0, fontFamily: SHRED_FONTS.body, fontWeight: 600, fontSize: `${sizes.boardName}px` }}>{entry.username}</Typography>
          <IneligibleChip label={entry.ineligibleLabel} sx={{ fontSize: `${sizes.boardLevel}px` }} />
          <LevelChip level={entry.level} sx={{ fontSize: `${sizes.boardLevel}px` }} />
          <Typography sx={{ flex: 'none', fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.boardPct}px`, color: pctColor(entry.improvementPct) }}>{formatPct(entry.improvementPct)}</Typography>
        </Box>
      ))}
      {rows.length === 0 && <Typography sx={{ color: SHRED_COLORS.muted, fontSize: `${sizes.boardName}px` }}>{SHRED_COPY.EMPTY_LIVE}</Typography>}
    </Box>
  );
};

const BoardPanel = ({ entries, minSessions, sizes }) => {
  const { rows, lineup } = getTvBoardRows(entries);
  return <BoardRows rows={rows} lineup={lineup} minSessions={minSessions} sizes={sizes} />;
};

const SpotlightPanel = ({ entries, minSessions, sizes, seed }) => (
  <BoardRows rows={getTvSpotlightRows(entries, seed)} lineup minSessions={minSessions} sizes={sizes} />
);

const CrewPanel = ({ crew, sizes }) => {
  const progress = getCrewProgress(crew);
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '43px', textAlign: 'center' }}>
      <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.crewPct}px`, lineHeight: 1, color: SHRED_COLORS.gold }}>
        {progress.displayPct}%
      </Typography>
      <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.crewText}px`, color: SHRED_COLORS.muted }}>
        {formatCompact(crew.totalWork)} of {formatCompact(crew.goalWork)} work · {crew.participants} members · {crew.sessions} sessions
      </Typography>
      <Box sx={{ mx: '38px', height: `${sizes.crewBar}px`, borderRadius: `${sizes.crewBar / 2}px`, background: 'rgba(255,255,255,0.1)', overflow: 'hidden' }}>
        <Box sx={{ height: '100%', width: `${progress.pct}%`, background: SHRED_COLORS.heat, boxShadow: '0 0 30px rgba(255,90,31,0.7)' }} />
      </Box>
    </Box>
  );
};

const FeedPanel = ({ feed, sizes }) => {
  const items = feed
    .map((event) => ({ event, text: buildFeedSentence(event) }))
    .filter((item) => item.text)
    .slice(0, sizes.feedCount);
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: `${sizes.feedGap}px`, overflow: 'hidden' }}>
      {items.length === 0 && <Typography sx={{ color: SHRED_COLORS.muted, fontSize: `${sizes.feedText}px` }}>{SHRED_COPY.FEED_EMPTY}</Typography>}
      {items.map(({ event, text }, index) => (
        <Typography key={`${event.type}-${event.id}-${index}`} sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.feedText}px`, display: 'flex', gap: '23px', lineHeight: 1.2 }}>
          <span aria-hidden="true">{SHRED_FEED_ICONS[event.type]}</span>
          <Box component="span" sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.feedDay}px`, color: SHRED_COLORS.flame, alignSelf: 'center' }}>D{event.day}</Box>
          <span>{text}</span>
        </Typography>
      ))}
    </Box>
  );
};

const CountdownPanel = ({ challenge, nowMs, unavailable, loading, sizes }) => {
  const countdown = unavailable || loading ? null : formatCountdown(getCountdownTarget(challenge, nowMs), nowMs);
  if (!countdown) {
    return (
      <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
        <Typography role="status" sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.status}px`, color: loading ? SHRED_COLORS.muted : SHRED_COLORS.gold }}>
          {loading ? SHRED_COPY.LOADING_STANDINGS : SHRED_COPY.LOAD_ERROR}
        </Typography>
      </Box>
    );
  }
  return (
    <Box sx={{ height: '100%', display: 'grid', placeItems: 'center', textAlign: 'center' }}>
      <Box>
        <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.countdownLabel}px`, letterSpacing: '0.2em', color: SHRED_COLORS.muted }}>
          {SHRED_COPY.COUNTDOWN_UPCOMING.toUpperCase()}
        </Typography>
        <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.countdownValue}px`, color: SHRED_COLORS.gold, lineHeight: 1.1 }}>
          {`${countdown.days}d ${String(countdown.hours).padStart(2, '0')}h ${String(countdown.minutes).padStart(2, '0')}m`}
        </Typography>
        <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.countdownText}px`, color: SHRED_COLORS.muted }}>{SHRED_COPY.EMPTY_UPCOMING}</Typography>
      </Box>
    </Box>
  );
};

const VaultTile = ({ prize, index, sizes }) => {
  const allItems = prize.items && prize.items.length > 0 ? prize.items : [prize.text];
  const physicalItems = allItems.filter((item) => getItemArtKey(item) !== 'title');
  const hasTitleItem = physicalItems.length < allItems.length;
  const shownItems = physicalItems.length > 0 ? physicalItems : allItems;
  const unlockMs = SHRED_TV_VAULT_FIRST_UNLOCK_MS + index * SHRED_TV_VAULT_UNLOCK_STEP_MS;
  return (
    <Box sx={{ position: 'relative', minWidth: 0, containerType: 'size', borderRadius: '24px', border: `3px solid ${prize.accent}66`, background: `linear-gradient(160deg, ${prize.accent}26, rgba(255,255,255,0.03) 70%)`, overflow: 'hidden' }}>
      <Box
        className="shred-motion shred-vault-lock"
        sx={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', background: SHRED_COLORS.panel, animation: `shredVaultShake ${SHRED_TV_VAULT_ANIM_MS}ms ${unlockMs - SHRED_TV_VAULT_ANIM_MS}ms ease both, shredVaultHide 1ms ${unlockMs}ms forwards` }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <Box aria-hidden="true" sx={{ fontSize: `${sizes.vaultIcon}px`, lineHeight: 1 }}>🔒</Box>
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, letterSpacing: '0.2em', color: SHRED_COLORS.muted, fontSize: `${sizes.vaultPrize}px` }}>{SHRED_COPY.TV_VAULT_LOCKED_HINT.toUpperCase()}</Typography>
        </Box>
      </Box>
      <Box
        className="shred-motion shred-vault-reveal"
        sx={{ height: '100%', p: '14px 20px', display: 'flex', flexDirection: 'column', justifyContent: 'center', textAlign: 'center', gap: '8px', overflow: 'hidden', opacity: 0, animation: `shredVaultPop ${SHRED_TV_VAULT_ANIM_MS}ms ${unlockMs}ms cubic-bezier(0.2, 1.4, 0.4, 1) both` }}
      >
        <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.08em', fontSize: `${sizes.vaultTitle * 0.62}px`, lineHeight: 1, color: prize.accent }}>
          <span aria-hidden="true">{prize.icon} </span>{prize.title}
        </Typography>
        {hasTitleItem && <Typography sx={{ fontFamily: SHRED_FONTS.numeric, letterSpacing: '0.15em', fontSize: `${sizes.vaultPrize * 0.6}px`, lineHeight: 1, color: SHRED_COLORS.muted }}>{SHRED_COPY.TV_VAULT_TITLE_NOTE}</Typography>}
        <Box sx={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'stretch', justifyContent: 'center', gap: '18px' }}>
          {shownItems.map((item) => (
            <Box key={item} sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: '8px', '@container (max-height: 250px)': { flexDirection: 'row', gap: '14px', textAlign: 'left' } }}>
              <ItemArt fit text={item} size={sizes.vaultIcon * (shownItems.length > 1 ? 1.5 : 1.9)} />
              <Typography sx={{ fontFamily: SHRED_FONTS.body, fontWeight: 600, flex: 'none', minWidth: 0, fontSize: `${sizes.vaultPrize}px`, lineHeight: 1.15, color: SHRED_COLORS.text, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden', '@container (max-height: 250px)': { flex: '1 1 0', fontSize: `${sizes.vaultPrize * 0.85}px` } }}>{item}</Typography>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const VaultPanel = ({ prizes, sizes }) => {
  const awardPrizes = prizes.filter((prize) => prize.id !== SHRED_PRIZE_IDS.CREW && prize.id !== SHRED_PRIZE_IDS.PODIUM);
  const crew = prizes.find((prize) => prize.id === SHRED_PRIZE_IDS.CREW);
  const crewDelayMs = SHRED_TV_VAULT_FIRST_UNLOCK_MS + awardPrizes.length * SHRED_TV_VAULT_UNLOCK_STEP_MS;
  const rows = Math.ceil(awardPrizes.length / sizes.vaultCols);
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', gap: '22px' }}>
      <Box sx={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: `repeat(${sizes.vaultCols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, 1fr)`, gap: '22px' }}>
        {awardPrizes.map((prize, index) => (
          <VaultTile key={prize.id} prize={prize} index={index} sizes={sizes} />
        ))}
      </Box>
      {crew && (
        <Box
          className="shred-motion shred-vault-reveal"
          sx={{ flex: 'none', borderRadius: '24px', border: `3px solid ${SHRED_COLORS.gold}88`, background: `linear-gradient(90deg, ${SHRED_COLORS.gold}30, transparent)`, p: '22px 32px', opacity: 0, animation: `shredVaultPop ${SHRED_TV_VAULT_ANIM_MS}ms ${crewDelayMs}ms cubic-bezier(0.2, 1.4, 0.4, 1) both` }}
        >
          <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, textTransform: 'uppercase', fontSize: `${sizes.vaultCrewTitle}px`, color: SHRED_COLORS.gold }}>{crew.icon} {crew.title}</Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.vaultCrewText}px`, color: SHRED_COLORS.text, lineHeight: 1.25 }}>{crew.text}</Typography>
        </Box>
      )}
    </Box>
  );
};

const PrizeDrop = ({ prizes, sizes, paused }) => {
  const [drop, setDrop] = useState(null);
  const counterRef = useRef(0);
  const prizesRef = useRef(prizes);
  prizesRef.current = prizes;
  const hasPrizes = prizes.length > 0;
  useEffect(() => {
    if (paused || !hasPrizes) return undefined;
    let showTimer;
    let nextTimer;
    const schedule = (isFirst) => {
      nextTimer = window.setTimeout(() => {
        counterRef.current += 1;
        setDrop({ key: counterRef.current, prize: pickRandomPrize(prizesRef.current, Math.random()) });
        showTimer = window.setTimeout(() => {
          setDrop(null);
          schedule(false);
        }, SHRED_TV_PRIZE_DROP_SHOW_MS);
      }, getNextPrizeDropDelayMs(Math.random(), isFirst));
    };
    schedule(true);
    return () => {
      window.clearTimeout(nextTimer);
      window.clearTimeout(showTimer);
      setDrop(null);
    };
  }, [paused, hasPrizes]);
  if (!drop || !drop.prize) return null;
  const { prize } = drop;
  return (
    <Box
      key={drop.key}
      role="status"
      className="shred-motion"
      sx={{ position: 'absolute', left: '50%', bottom: '120px', width: '86%', zIndex: 5, transform: 'translateX(-50%)', borderRadius: '32px', border: `4px solid ${prize.accent}`, background: `linear-gradient(135deg, ${SHRED_COLORS.ink}f2, ${prize.accent}55)`, boxShadow: `0 0 80px ${prize.accent}99`, p: '28px 40px', display: 'flex', alignItems: 'center', gap: '32px', animation: `shredPrizeDrop ${SHRED_TV_PRIZE_DROP_SHOW_MS}ms ease both` }}
    >
      <PrizeIcon prize={prize} size={sizes.dropIcon} />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontFamily: SHRED_FONTS.numeric, letterSpacing: '0.25em', fontSize: `${sizes.dropLabel}px`, color: SHRED_COLORS.gold }}><span aria-hidden="true">💥 </span>{SHRED_COPY.TV_PRIZE_DROP_LABEL.toUpperCase()}<span aria-hidden="true"> 💥</span></Typography>
        <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, textTransform: 'uppercase', fontSize: `${sizes.dropTitle}px`, lineHeight: 1.05, color: prize.accent }}>{prize.title}</Typography>
        <Typography sx={{ fontFamily: SHRED_FONTS.body, fontSize: `${sizes.dropText}px`, lineHeight: 1.2 }}>{prize.text}</Typography>
      </Box>
      {['✨', '⭐', '✨'].map((spark, index) => (
        <Box key={index} aria-hidden="true" className="shred-motion" sx={{ position: 'absolute', top: `${-24 + index * 10}px`, right: `${40 + index * 120}px`, fontSize: '48px', animation: `shredTwinkle 900ms ${index * 250}ms ease-in-out infinite` }}>{spark}</Box>
      ))}
    </Box>
  );
};

// Full-screen lobby view for landscape and vertical TVs: panels rotate on a timer, and live polling keeps
// the data fresh underneath.
const Tv = ({ view, status, skewMs, unavailable, loading }) => {
  const nowMs = useNow(1000, skewMs);
  const { challenge, entries, crew, feed, awards } = view;
  const prizes = getPrizeList(awards, challenge.rewards);
  const { portrait, stageWidth, stageHeight, scale } = useStageLayout();
  const sizes = SIZES[portrait ? 'portrait' : 'landscape'];
  const panels = unavailable || loading || status === SHRED_STATUS.UPCOMING ? [PANELS.COUNTDOWN] : [PANELS.PODIUM, PANELS.BOARD, ...(entries.length > 0 ? [PANELS.RANDOM] : []), ...(prizes.length > 0 ? [PANELS.VAULT] : []), PANELS.CREW, PANELS.FEED];
  const [index, setIndex] = useState(0);
  const [seedBase] = useState(() => Math.floor(Math.random() * SEED_RANGE));
  useEffect(() => {
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, []);
  useEffect(() => {
    if (panels.length < 2) return undefined;
    const timer = window.setInterval(() => setIndex((value) => value + 1), SHRED_TV_ROTATE_MS);
    return () => window.clearInterval(timer);
  }, [panels.length]);
  const active = panels[index % panels.length];
  const day = getCurrentDay(challenge, nowMs);

  return (
    <Box
      data-tv-orientation={portrait ? 'portrait' : 'landscape'}
      sx={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        overflow: 'hidden',
        color: SHRED_COLORS.text,
        background: `radial-gradient(ellipse 90% 60% at 50% 120%, rgba(255,59,48,0.45), transparent 65%), ${SHRED_COLORS.ink}`,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          width: `${stageWidth}px`,
          height: `${stageHeight}px`,
          transform: `scale(${scale})`,
          transformOrigin: 'top left',
          display: 'flex',
          flexDirection: 'column',
          px: `${sizes.padX}px`,
          py: `${sizes.padY}px`,
          boxSizing: 'border-box',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 2 }}>
          <Typography component="h1" sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 800, fontSize: `${sizes.title}px`, lineHeight: 1, textTransform: 'uppercase', background: 'linear-gradient(180deg,#ffe28a,#ff8a1f 55%,#ff3b30)', WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent' }}>
            {SHRED_COPY.TITLE_BOTTOM}
          </Typography>
          <Typography sx={{ fontFamily: SHRED_FONTS.numeric, fontSize: `${sizes.day}px`, color: SHRED_COLORS.muted, whiteSpace: 'nowrap' }}>
            {day > 0 ? `DAY ${day} OF ${challenge.dayCount}` : ''}
          </Typography>
        </Box>
        <Typography sx={{ fontFamily: SHRED_FONTS.display, fontWeight: 700, fontSize: `${sizes.panelTitle}px`, textTransform: 'uppercase', mt: `${sizes.panelTitleGap}px`, mb: `${sizes.panelTitleGap}px` }}>
          {active === PANELS.BOARD && getTvBoardRows(entries).lineup && entries.length > 0 ? SHRED_COPY.TV_LINEUP_TITLE : SHRED_COPY.TV_TITLES[active]}
        </Typography>
        <Box key={active} className="shred-motion" sx={{ flex: 1, minHeight: 0, animation: 'shredRise 600ms ease both' }}>
          {active === PANELS.PODIUM && <PodiumPanel entries={entries} sizes={sizes} />}
          {active === PANELS.BOARD && <BoardPanel entries={entries} minSessions={challenge.minSessions} sizes={sizes} />}
          {active === PANELS.RANDOM && <SpotlightPanel entries={entries} minSessions={challenge.minSessions} sizes={sizes} seed={seedBase + Math.floor(index / panels.length)} />}
          {active === PANELS.VAULT && <VaultPanel prizes={prizes} sizes={sizes} />}
          {active === PANELS.CREW && <CrewPanel crew={crew} sizes={sizes} />}
          {active === PANELS.FEED && <FeedPanel feed={feed} sizes={sizes} />}
          {active === PANELS.COUNTDOWN && <CountdownPanel challenge={challenge} nowMs={nowMs} unavailable={unavailable} loading={loading} sizes={sizes} />}
        </Box>
        <PrizeDrop prizes={prizes} sizes={sizes} paused={active === PANELS.VAULT || panels.length < 2} />
        <Box aria-hidden="true" sx={{ display: 'flex', gap: 1, justifyContent: 'center', pt: `${sizes.dots}px` }}>
          {panels.map((panel, i) => (
            <Box key={panel} sx={{ width: i === index % panels.length ? 36 : 12, height: 8, borderRadius: 4, background: i === index % panels.length ? SHRED_COLORS.heat : 'rgba(255,255,255,0.2)' }} />
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default Tv;
